"use client"

import { useState } from "react"
import ReactJson from "react-json-view"
import {
  Box, Button, Flex, FormControl, FormLabel,
  Select, Textarea,
  useToast, VStack,
  Tabs, TabList, TabPanels, Tab, TabPanel
} from "@chakra-ui/react"
import { useColorMode } from "@chakra-ui/react"
import { toastMessage } from "../../utils/toast"
import PageHeading from "../../components/PageHeading"


export default function JsonFormatter() {
  const [viewMode, setViewMode] = useState("text")
  const [jsonInput, setJsonInput] = useState("")
  const [spacing, setSpacing] = useState("2")
  const toast = useToast()
  const { colorMode } = useColorMode();

  const validateAndFormat = () => {
    try {
      let fixedInput = jsonInput
        .replace(/'/g, '"') // single quotes to double quotes
        .replace(/([{,])\s*([^"\d][\w$]*)\s*:/g, '$1"$2":') //Fix missing quotes around keys (avoid numbers)
        .replace(/,\s*([}\]])/g, '$1'); // Remove trailing commas before closing braces/brackets

      //fix missing comma
      const maxAttempts = 100;
      let attempts = 0;
      while (attempts < maxAttempts) {
        try {
          JSON.parse(fixedInput);
          break;
        } catch (error) {
          const match = error.message.match(/at position (\d+)/);
          if (!match) throw error; // If no position is found, rethrow the error

          const errorPos = parseInt(match[1], 10);
          fixedInput = fixedInput.slice(0, errorPos) + ',' + fixedInput.slice(errorPos); //inserting comma at the error position
          attempts++;
        }
      }

      let parsedObject = JSON.parse(fixedInput);
      if (typeof parsedObject === "string") {
        parsedObject = JSON.parse(parsedObject);
      }

      const formattedJson = JSON.stringify(parsedObject, null, Number.parseInt(spacing))
      setJsonInput(formattedJson)
      toastMessage(toast, "success", "JSON formatted");
    } catch (error) {
      toastMessage(toast, "error", "Invalid JSON", error.message);
    }
  }

  const minifyJson = () => {
    try {
      const parsedJson = JSON.parse(jsonInput)
      const minifiedJson = JSON.stringify(parsedJson)
      setJsonInput(minifiedJson)
      toastMessage(toast, "success", "JSON minified");
    } catch (error) {
      toastMessage(toast, "success", "Invalid JSON");
    }
  }

  const removeQuotesOnKeys = () => {
    try {
      const parsedJson = JSON.parse(jsonInput);
      const withoutQuotes = JSON.stringify(parsedJson, null, Number.parseInt(spacing))
        .replace(/"([a-zA-Z_$][\w$]*)":/g, '$1:'); // Keep quotes on invalid keys
      setJsonInput(withoutQuotes);
      toastMessage(toast, "success", "Removed quotes from keys");
    } catch (error) {
      toastMessage(toast, "success", "Invalid JSON");
    }
  }

  const stringifyJson = () => {
    try {
      const parsedJson = JSON.parse(jsonInput);
      const stringifiedJson = JSON.stringify(JSON.stringify(parsedJson));
      setJsonInput(stringifiedJson);
      toastMessage(toast, "success", "JSON stringified");
    } catch (error) {
      toastMessage(toast, "error", "Invalid JSON", error.message);
    }
  }

  const safeParse = (input) => {
    try {
      return JSON.parse(input);
    } catch {
      return {};
    }
  }

  const handleTreeEdit = (updatedSrc) => {
    setJsonInput(JSON.stringify(updatedSrc, null, Number.parseInt(spacing)));
  }

  return (
    <VStack spacing={6} align="stretch">
      <PageHeading
        mainText={`JSON Formatter & Validator`}
        subText={`A free online tool to validate, format, minify, and customize your JSON. Easily switch between quoted and unquoted keys while maintaining a valid JSON structure.`}
      />

      <Flex direction={{ base: "column", md: "row" }} gap={4} mt={"12px"}>
      <VStack spacing={4} align="stretch" w={{ base: "100%", md: "250px" }} marginTop={{ base: 0, md: "40px" }} order={{ base: 1, md: 2 }}>
          <Box p={4} borderWidth="1px" borderRadius="md">
            <FormControl mb={4}>
              <FormLabel>Indentation</FormLabel>
              <Select value={spacing} onChange={(e) => setSpacing(e.target.value)}>
                <option value="2">2 Spaces</option>
                <option value="4">4 Spaces</option>
                <option value="6">6 Spaces</option>
                <option value="8">8 Spaces</option>
                <option value="0">No spacing</option>
              </Select>
            </FormControl>
          </Box>

          <Button colorScheme="blue" onClick={validateAndFormat} isDisabled={viewMode !== "text"}>
            Validate and Format
          </Button>

          <Button colorScheme="blue" onClick={minifyJson} isDisabled={viewMode !== "text"}>
            Minify
          </Button>

          <Button colorScheme="blue" onClick={stringifyJson} isDisabled={viewMode !== "text"}>
            Stringify
          </Button>

          <Button colorScheme="blue" onClick={removeQuotesOnKeys} isDisabled={viewMode !== "text"}>
            Remove "" on JSON keys
          </Button>
        </VStack>

        <Box flex={1} order={{ base: 2, md: 1 }}>
          <Tabs
            variant={"line"}
            onChange={(index) => setViewMode(index === 0 ? "text" : "tree")}
          >
            <TabList>
              <Tab>Text View</Tab>
              <Tab>Tree View</Tab>
            </TabList>

            <TabPanels>
              <TabPanel paddingX={0} paddingY={"4px"}>
                <Textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder='Enter your JSON here. Example: {"name": "SubjectiveOne"}'
                  height="70vh"
                  fontFamily="monospace"
                />
              </TabPanel>

              <TabPanel paddingX={"4px"} paddingY={"4px"}>
                {/* <Box height="70vh" overflow="auto" borderWidth="1px" borderRadius="md" p={2}> */}
                  <ReactJson
                    src={safeParse(jsonInput)}
                    name={false}
                    onEdit={(edit) => handleTreeEdit(edit.updated_src)}
                    onAdd={(add) => handleTreeEdit(add.updated_src)}
                    onDelete={(del) => handleTreeEdit(del.updated_src)}
                    enableClipboard={false}
                    displayDataTypes={false}
                    collapsed={3}
                    indentWidth={spacing}
                    theme={colorMode === "light" ? "rjv-default" : "twilight"}
                    iconStyle="square"
                  />
                {/* </Box> */}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

      </Flex>
    </VStack>
  )
}

